import { WebApiService } from '../../../plugins/webApiService'
import { Events } from '../../../plugins/events'
import EventToAsyncDecorator from '../../../plugins/eventToAsyncDecorator'
import WsResponseModelT from '../../../SchemaBuilderServer/Models/WsResponseModelT';
import GetGoalSurveyStatisticsResponse from '../../../Spirit/Admin/Interfaces/Models/StatisticsService/GetGoalSurveyStatisticsResponse';
import GetMAUResponse from '../../../Spirit/Admin/Interfaces/Models/StatisticsService/GetMAUResponse';
import GetRegistrationStatisticResponse from '../../../Spirit/Admin/Interfaces/Models/StatisticsService/GetRegistrationStatisticResponse';
import GetTopTracksResponse from '../../../Spirit/Admin/Interfaces/Models/StatisticsService/GetTopTracksResponse';
import GetTopAffirmationResponse from '../../../Spirit/Admin/Interfaces/Models/StatisticsService/GetTopAffirmationResponse';
import GetPaidUsersResponse from '../../../Spirit/Admin/Interfaces/Models/StatisticsService/GetPaidUsersResponse';
import GetPromoCodeRedemptionsResponse from '../../../Spirit/Admin/Interfaces/Models/StatisticsService/GetPromoCodeRedemptionsResponse';

export interface StatisticDateRangeRequest {
	DateStart?: Date | string;
	DateEnd?: Date | string;
}

export interface StatisticTopTracksRequest extends StatisticDateRangeRequest {
	Take?: number;
}

export interface StatisticPaidUsersRequest {
	SubscriptionStatus?: 'Free' | 'Active' | 'Expired';
	Page?: { Skip?: number; Take?: number };
}

export interface StatisticPromoRedemptionsRequest extends StatisticDateRangeRequest {
	Code?: string;
	Page?: { Skip?: number; Take?: number };
}

export default class StatisticsService {
	webApiService: WebApiService;
	GetGoalSurveyStatisticsEvent = new Events<WsResponseModelT<GetGoalSurveyStatisticsResponse>>();
	GetMauEvent = new Events<WsResponseModelT<GetMAUResponse>>();
	GetDauEvent = new Events<WsResponseModelT<GetMAUResponse>>();
	GetRegistrationsEvent = new Events<WsResponseModelT<GetRegistrationStatisticResponse>>();
	GetTopTracksEvent = new Events<WsResponseModelT<GetTopTracksResponse>>();
	GetTopAffirmationsEvent = new Events<WsResponseModelT<GetTopAffirmationResponse>>();
	GetPaidUsersEvent = new Events<WsResponseModelT<GetPaidUsersResponse>>();
	GetPromoRedemptionsEvent = new Events<WsResponseModelT<GetPromoCodeRedemptionsResponse>>();

	constructor(webApiService: WebApiService) {
		const self = this;
		this.webApiService = webApiService;
		webApiService.on('StatisticsService', 'GetGoalSurveyStatistics', (res: WsResponseModelT<GetGoalSurveyStatisticsResponse>) => {
			self.GetGoalSurveyStatisticsEvent.trigger(res);
		});
		webApiService.on('StatisticsService', 'GetMau', (res: WsResponseModelT<GetMAUResponse>) => {
			self.GetMauEvent.trigger(res);
		});
		webApiService.on('StatisticsService', 'GetDau', (res: WsResponseModelT<GetMAUResponse>) => {
			self.GetDauEvent.trigger(res);
		});
		webApiService.on('StatisticsService', 'GetRegistrations', (res: WsResponseModelT<GetRegistrationStatisticResponse>) => {
			self.GetRegistrationsEvent.trigger(res);
		});
		webApiService.on('StatisticsService', 'GetTopTracks', (res: WsResponseModelT<GetTopTracksResponse>) => {
			self.GetTopTracksEvent.trigger(res);
		});
		webApiService.on('StatisticsService', 'GetTopAffirmations', (res: WsResponseModelT<GetTopAffirmationResponse>) => {
			self.GetTopAffirmationsEvent.trigger(res);
		});
		webApiService.on('StatisticsService', 'GetPaidUsers', (res: WsResponseModelT<GetPaidUsersResponse>) => {
			self.GetPaidUsersEvent.trigger(res);
		});
		webApiService.on('StatisticsService', 'GetPromoRedemptions', (res: WsResponseModelT<GetPromoCodeRedemptionsResponse>) => {
			self.GetPromoRedemptionsEvent.trigger(res);
		});
	}

	GetGoalSurveyStatistics() {
		this.webApiService.send('StatisticsService', 'GetGoalSurveyStatistics');
	}

	GetMau() {
		this.webApiService.send('StatisticsService', 'GetMau');
	}

	GetDau() {
		this.webApiService.send('StatisticsService', 'GetDau');
	}

	GetRegistrations(request: StatisticDateRangeRequest = null) {
		this.webApiService.send('StatisticsService', 'GetRegistrations', request);
	}

	GetTopTracks(request: StatisticTopTracksRequest = null) {
		this.webApiService.send('StatisticsService', 'GetTopTracks', request);
	}

	GetTopAffirmations(request: StatisticDateRangeRequest = null) {
		this.webApiService.send('StatisticsService', 'GetTopAffirmations', request);
	}

	GetPaidUsers(request: StatisticPaidUsersRequest = null) {
		this.webApiService.send('StatisticsService', 'GetPaidUsers', request);
	}

	GetPromoRedemptions(request: StatisticPromoRedemptionsRequest = null) {
		this.webApiService.send('StatisticsService', 'GetPromoRedemptions', request);
	}

	async getGoalSurveyStatisticsAsync(): Promise<GetGoalSurveyStatisticsResponse> {
		return EventToAsyncDecorator.getInstance().bind(this.GetGoalSurveyStatistics.bind(this), this.GetGoalSurveyStatisticsEvent)();
	}

	async getMauAsync(): Promise<GetMAUResponse> {
		return EventToAsyncDecorator.getInstance().bind(this.GetMau.bind(this), this.GetMauEvent)();
	}

	async getDauAsync(): Promise<GetMAUResponse> {
		return EventToAsyncDecorator.getInstance().bind(this.GetDau.bind(this), this.GetDauEvent)();
	}

	async getRegistrationsAsync(request: StatisticDateRangeRequest = null): Promise<GetRegistrationStatisticResponse> {
		return EventToAsyncDecorator.getInstance().bind(this.GetRegistrations.bind(this), this.GetRegistrationsEvent)(request);
	}

	async getTopTracksAsync(request: StatisticTopTracksRequest = null): Promise<GetTopTracksResponse> {
		return EventToAsyncDecorator.getInstance().bind(this.GetTopTracks.bind(this), this.GetTopTracksEvent)(request);
	}

	async getTopAffirmationsAsync(request: StatisticDateRangeRequest = null): Promise<GetTopAffirmationResponse> {
		return EventToAsyncDecorator.getInstance().bind(this.GetTopAffirmations.bind(this), this.GetTopAffirmationsEvent)(request);
	}

	async getPaidUsersAsync(request: StatisticPaidUsersRequest = null): Promise<GetPaidUsersResponse> {
		return EventToAsyncDecorator.getInstance().bind(this.GetPaidUsers.bind(this), this.GetPaidUsersEvent)(request);
	}

	async getPromoRedemptionsAsync(request: StatisticPromoRedemptionsRequest = null): Promise<GetPromoCodeRedemptionsResponse> {
		return EventToAsyncDecorator.getInstance().bind(this.GetPromoRedemptions.bind(this), this.GetPromoRedemptionsEvent)(request);
	}
}
